import React from "react";
import mobileImage from "../Image/hero_sec_img.png";
import { Container, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { m, LazyMotion, domAnimation } from "framer-motion";
import PlayStore from "../Image/Play-Store.png";
import AppStore from "../Image/App-Store.png";

const HeroSection = () => {
  const { t } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2, // Increased stagger slightly for visual clarity
        delayChildren: 0.5,   // Added delay to let the browser paint the LCP and settle hydration
        when: "beforeChildren",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8, // Slightly longer duration for smoother feel
        ease: [0.25, 0.1, 0.25, 1.0], // cubic-bezier for smooth "Apple-like" easing
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1.0,
        ease: "easeInOut",
        delay: 0.2,
      },
    },
  };

  const iconHoverVariants = {
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
  };

  return (
    <LazyMotion features={domAnimation}>
      <section className="application pt-5 p-lg-0 hero-section-container" style={{ overflow: "hidden" }}>
        <Container>
          <Row className="align-items-center">
            <Col
              sm={12}
              md={6}
              className="p-0 px-lg-0 px-md-0 px-3 text-lg-start text-md-start text-center"
            >
              <m.div
                variants={containerVariants}
                initial="hidden"
                animate="visible" // Changed from whileInView to animate for Hero section to ensure it runs immediately reliably without observer overhead
                viewport={{ once: true }}
              >
                <m.p
                  variants={itemVariants}
                  className="DownloadappTxt1 mb-lg-3 mb-md-3 mb-sm-2 mb-1 fw-bold fs-5"
                  style={{ color: "var(--text-dark)", willChange: "transform, opacity" }}
                >
                  🌸 {t("heroTitle2")} 🌸
                </m.p>
                <m.h2
                  variants={itemVariants}
                  className="DownloadappTxt2 mb-lg-3 mb-md-3 mb-sm-2 mb-2 display-4 fw-bold"
                  style={{ color: "var(--primary-color)", willChange: "transform, opacity" }}
                >
                  {t("heroTitle")}
                </m.h2>
                <m.p
                  variants={itemVariants}
                  className="DownloadappTxt3 mb-lg-3 mb-md-3 mb-sm-2 mb-3"
                  style={{ color: "var(--text-light)", willChange: "transform, opacity" }}
                >
                  {t("heroDescription")}
                </m.p>

                {/* App Download Buttons */}
                <m.div
                  variants={itemVariants}
                  className="d-flex align-items-center justify-content-center justify-content-lg-start justify-content-md-start mb-4 gap-1"
                >
                  <m.a
                    href="https://play.google.com/store/apps/details?id=com.nitya.mala.app"
                    whileHover={{ scale: 1.03, transition: { duration: 0.2, ease: "easeOut" } }}
                    whileTap={{ scale: 0.95 }}
                    target="_blank"
                    rel="noreferrer"
                    className="d-inline-block"
                  >
                    <img
                      src={PlayStore}
                      width="150px"
                      alt="Download on the App Store"
                      loading="eager"
                    />
                  </m.a>
                  <m.a
                    href="https://play.google.com/store/apps/details?id=com.nitya.mala.app"
                    whileHover={{ scale: 1.03, transition: { duration: 0.2, ease: "easeOut" } }}
                    whileTap={{ scale: 0.95 }}
                    target="_blank"
                    rel="noreferrer"
                    className="d-inline-block"
                  >
                    <img
                      src={AppStore}
                      width="150px"
                      alt="Get it on Google Play"
                      loading="eager"
                    />
                  </m.a>
                </m.div>

                {/* Social Icons */}
                <m.div
                  variants={itemVariants}
                  className="social_container d-flex align-items-center justify-content-center justify-content-lg-start justify-content-md-start gap-1"
                >
                  {[
                    {
                      href: "https://www.facebook.com/profile.php?id=61570826912508",
                      icon: "bi bi-facebook",
                    },
                    {
                      href: "https://www.instagram.com/nityamala_com/",
                      icon: "bi bi-instagram",
                    },
                    {
                      href: "https://youtube.com/@nityamala?si=aPVdgzgspapoDRTc",
                      icon: "bi bi-youtube",
                    },
                    {
                      href: "https://www.facebook.com/profile.php?id=61570826912508",
                      icon: "bi bi-telegram",
                    },
                    {
                      href: "https://www.threads.net/@nityamala_51",
                      icon: "bi bi-threads",
                    },
                    {
                      href: "https://www.facebook.com/profile.php?id=61570826912508",
                      icon: "bi bi-whatsapp",
                    },
                  ].map((social, index) => (
                    <m.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      className="social-icon_footer"
                      variants={iconHoverVariants}
                      whileHover="hover"
                    >
                      <i className={social.icon}></i>
                    </m.a>
                  ))}
                </m.div>
              </m.div>
            </Col>

            <Col sm={12} md={6} className="py-3 text-center">
              <m.img
                variants={imageVariants}
                initial="hidden"
                animate="visible"
                src={mobileImage}
                alt="mobileImage"
                className="mobileImageHero"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{ maxHeight: "80vh", objectFit: "contain", willChange: "opacity" }}
              />
            </Col>
          </Row>
        </Container>
      </section>
    </LazyMotion>
  );
};

export default HeroSection;
